import { useRouter } from 'next/router';
import classNames from 'classnames';
import Skeleton from 'react-loading-skeleton';
import { Props } from './interfaces';
import styles from './shipping-information.module.scss';
import { CurrencyHelpers } from '../../../../../../../../helpers/currencyHelpers';
import { useFormat } from '../../../../../../../../helpers/hooks/useFormat';
import QuestionInfo from '../../../../../../atoms/question-info/question-info';
import { OrderState } from '../../interfaces';

const ShippingInformation = ({ order, className }: Props) => {
  const { formatMessage: formatOrdersMessage } = useFormat({ name: 'orders' });
  const { locale } = useRouter();

  const shippingAddress = order?.shippingAddress;

  const getShippingCost = () => {
    const price = order?.shippingInfo.price;

    if (!price || price?.centAmount === 0) {
      return 'FREE';
    } else {
      return CurrencyHelpers.formatForCurrency(price.centAmount, locale, price.currencyCode, price.fractionDigits);
    }
  };

  const isLoading = !order || !shippingAddress;

  return (
    <div className={classNames(styles['container'], className)}>
      <h3>
        {formatOrdersMessage({
          id: 'orderDetail.shippingInfo.title',
          defaultMessage: 'SHIPPING INFORMATION',
        })}
      </h3>
      {isLoading && (
        <>
          <Skeleton baseColor="#cdcdcd" width={200} height={25} />
          <Skeleton baseColor="#cdcdcd" width={200} height={15} />
          <Skeleton baseColor="#cdcdcd" width={200} height={15} />
          <Skeleton baseColor="#cdcdcd" width={200} height={15} />
          <Skeleton baseColor="#cdcdcd" height={20} style={{ marginTop: '20px' }} />
          <Skeleton baseColor="#cdcdcd" width={300} height={20} style={{ marginTop: '20px' }} />
        </>
      )}
      {!isLoading && order?.orderState !== OrderState.CANCELED && (
        <>
          <div className={styles['info']}>
            <h4>
              {shippingAddress?.firstName} {shippingAddress?.lastName}
            </h4>
            <p>{shippingAddress?.streetName}</p>
            {shippingAddress?.additionalStreetInfo && <p>{shippingAddress.additionalStreetInfo}</p>}
            <p>
              {shippingAddress?.city}, {shippingAddress?.state} {shippingAddress?.postalCode}
            </p>
            <p>{shippingAddress?.phone}</p>
            {!order ? <Skeleton baseColor="#cdcdcd" width={200} height={15} /> : <p>{order.email}</p>}
          </div>

          {order?.shippingInfo?.description && (
            <p className={styles['extra-info']}>
              {order?.shippingInfo?.description} <span>{getShippingCost()}</span>
            </p>
          )}

          {order?.signatureRequired && (
            <div className={styles['signature-required']}>
              <img title="checkmark" src="/icons/checkmark.svg" width={18} height={18} />
              <span>
                {formatOrdersMessage({
                  id: 'orderDetail.shippingInfo.signatureRequired',
                  defaultMessage: 'Signature required for delivery.',
                })}
              </span>
              <QuestionInfo
                overlay={formatOrdersMessage({
                  id: 'orderDetail.shippingInfo.signatureRequired.overlay',
                  defaultMessage: 'signature required overlay message',
                })}
                placement="bottom"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ShippingInformation;
